import { Coffee } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { useStartBreak } from '@/features/workMode/hooks';
import { describeWorkError } from '@/features/workMode/api';
import { BREAK_REASONS, type WorkSession } from '@/features/workMode/types';

interface WorkBreakDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
}

/** Pick why you're pausing. The unit timer stops until you resume. */
export function WorkBreakDialog({ open, onOpenChange, session }: WorkBreakDialogProps) {
  const start = useStartBreak();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Coffee className="h-5 w-5 text-info" />
            Pause the clock
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 px-6 py-5">
          {BREAK_REASONS.map((r) => (
            <button
              key={r.value}
              type="button"
              disabled={start.isPending}
              onClick={async () => {
                try {
                  await start.mutateAsync({ sessionId: session.id, reason: r.value });
                  onOpenChange(false);
                } catch (e) {
                  toast.error(describeWorkError(e));
                }
              }}
              className="flex min-h-[56px] items-center justify-center rounded-xl border border-border bg-card px-4 py-3 text-center hover:bg-secondary disabled:opacity-50"
            >
              <span className="text-base font-medium text-foreground">{r.label}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-end border-t border-border px-6 py-4">
          <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)}>
            Never mind
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
